"use client";

import React, { useState, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Sparkles, ChevronDown, ChevronUp, CheckCircle, AlertTriangle } from "lucide-react";
import type { BaseEntity } from "../../UniversalCRMTable/types/index";
import type { AIAssistantPanelProps } from "../types/ai-assistant";
import { useAIFormAssistant } from "../hooks/useAIFormAssistant";
import { formatExtractionResults } from "../utils/extractFormDataFromLLM";

/**
 * AI Assistant Panel Component
 * Provides inline chat interface for AI-assisted form filling
 * Positioned at the top of the form
 */
export function AIAssistantPanel<T extends BaseEntity = BaseEntity>({
  entityName,
  config,
  form,
  onDataExtracted,
}: AIAssistantPanelProps<T>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [userInput, setUserInput] = useState("");
  const { isLoading, error, extractedData, sendPrompt, clearError, reset } = useAIFormAssistant({
    entityName,
    config,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!userInput.trim() || isLoading) return;

      await sendPrompt(userInput);
    },
    [userInput, isLoading, sendPrompt]
  );

  const handleApplyData = useCallback(() => {
    if (!extractedData || !form) return;

    // Apply extracted data to form
    Object.entries(extractedData.data).forEach(([fieldName, value]) => {
      form.setValue(fieldName, value, { shouldValidate: true });
    });

    // Call callback if provided
    onDataExtracted?.(extractedData.data);

    // Reset the panel
    setUserInput("");
    reset();
    setIsExpanded(false);
  }, [extractedData, form, onDataExtracted, reset]);

  const handleDismiss = useCallback(() => {
    reset();
    setUserInput("");
  }, [reset]);

  return (
    <div className="mb-6 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-900 dark:text-blue-200">AI Assistant</span>
          <span className="text-sm text-blue-700 dark:text-blue-300">Get help filling this form</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-blue-600" />
        ) : (
          <ChevronDown className="h-5 w-5 text-blue-600" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-blue-200 dark:border-blue-800 px-4 py-4 space-y-4">
          {/* Input Section */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Describe what you want to create
              </label>
              <div className="flex flex-col gap-2">
                <Textarea
                  placeholder={`E.g., "Create a ${entityName.toLowerCase()} for John Doe with high priority and due date next Friday. Include notes about the project scope."`}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  disabled={isLoading}
                  className="flex-1 min-h-[100px] resize-none"
                />
                <Button
                  type="submit"
                  disabled={isLoading || !userInput.trim()}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Get AI Help
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-3 dark:bg-red-950/40 dark:border-red-800">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900 dark:text-red-200">{error.message}</p>
                  {Array.isArray(error.details) && error.details.length > 0 && (
                    <ul className="mt-1 text-xs text-red-800 dark:text-red-200 space-y-1">
                      {error.details.map((detail, idx) => (
                        <li key={idx}>• {String(detail)}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearError}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium text-sm"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Results Display */}
            {extractedData && !error && (
              <div className="space-y-3">
                {/* Success Message */}
                {extractedData.fieldsMatched.length > 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-start gap-3 dark:bg-green-950/40 dark:border-green-800">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900 dark:text-green-200">
                        Successfully extracted {extractedData.fieldsMatched.length} field(s)
                      </p>
                      <p className="text-xs text-green-800 dark:text-green-200 mt-1">
                        {extractedData.fieldsMatched.join(", ")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {extractedData.warnings.length > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-3 dark:bg-yellow-950/40 dark:border-yellow-800">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200">Notes</p>
                      <ul className="mt-1 text-xs text-yellow-800 dark:text-yellow-200 space-y-1">
                        {extractedData.warnings.map((warning, idx) => (
                          <li key={idx}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Missing Required Fields */}
                {extractedData.fieldsMissing.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md dark:bg-blue-950/40 dark:border-blue-800">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                      Missing required fields: {extractedData.fieldsMissing.join(", ")}
                    </p>
                    <p className="text-xs text-blue-800 dark:text-blue-200 mt-1">
                      You can fill these manually after applying the AI suggestions.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    onClick={handleApplyData}
                    className="flex-1"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Apply to Form
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDismiss}
                    className="flex-1"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}
          </form>

          {/* Help Text */}
          <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700">
            <p className="font-medium mb-1">💡 Tips:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Be specific about what you want to create</li>
              <li>Include relevant details like names, dates, and priorities</li>
              <li>You can edit any auto-filled fields before submitting</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
